/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useUpdate } from 'react-use';

import { isFeatureEnabled } from '@ibm-watson/shared/modules/ia/store/features/selectors';
import * as headerActions from '@ibm-watson/shared/modules/ia/store/header/actions';
import {
  PLAN_NAMES,
  PLAN_TITLES,
  PLAN_UPGRADE
} from '@ibm-watson/shared/modules/ia/store/plan/constants';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import PlusTrialManager from '@ibm-watson/shared/modules/ia/util/plusTrialManager';
import { useAppDispatch, useAppSelector } from '../../../store';
import { FeatureFlag } from '../../../ts/feature-flags';
import {
  selectPlusTrialData,
  isUpdatingInstance as selectIsUpdatingInstance,
  selectPlan
} from '../../ia/store/instance/selectors';
import { isInstanceReadOnly } from '../store/access-control/selectors';
import {
  isShowInstancePicker,
  isShowPlanInfo,
  isShowRestoreIA,
  selectActivePanel,
  isShowAboutModal,
  isShowCreateAssistantModal
} from '../store/header/selectors';
import { switchExperience } from '../store/instance/actions';
import {
  getAccount,
  getUser,
  selectInstanceId,
  selectInstanceRegion
} from '../store/session/selectors';
import { doLogout } from '../util/history';
import { ctaEvents } from '../util/trackers/segment';
import { messages } from './messages';

export const useHeader = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [isAddUsersModalOpen, setIsAddUsersModalOpen] = useState(false);
  const [isManageUserModalOpen, setIsManageUserModalOpen] = useState(false);
  const [showWhatsNewPanel, setShowWhatsNewPanel] = useState(false);

  const forceUpdate = useUpdate();
  const account = useAppSelector(getAccount);
  const instanceId = useAppSelector(selectInstanceId);

  const setShowInstancePicker = (show: boolean) =>
    dispatch(headerActions.setShowInstancePicker(show));
  const setShowPlanInfo = (show: boolean) =>
    dispatch(headerActions.setShowPlanInfo(show));
  const setShowRestoreIA = useCallback(
    show => {
      if (show) {
        ctaEvents.restoreClassicIaSelected();
      }
      dispatch(headerActions.setShowRestoreIA(show));
    },
    [dispatch]
  );

  const setActivePanel = useCallback(
    panel => dispatch(headerActions.setActivePanel(panel)),
    [dispatch]
  );
  const setShowAboutModal = (show: boolean) =>
    dispatch(headerActions.setShowAboutModal(show));
  const setShowCreateAssistantModal = (show: boolean) => {
    if (show) {
      ctaEvents.createAssistantClicked();
    }
    dispatch(headerActions.setShowCreateAssistantModal(show));
  };

  const handleOpenWhatsNewPanel = useCallback(() => {
    setShowWhatsNewPanel(true);
  }, [setShowWhatsNewPanel]);

  const showInstancePicker = useAppSelector(isShowInstancePicker);
  const showPlanInfo = useAppSelector(isShowPlanInfo);
  const showRestoreIA = useAppSelector(isShowRestoreIA);
  const showAboutModal = useAppSelector(isShowAboutModal);
  const showCreateAssistantModal = useAppSelector(isShowCreateAssistantModal);
  const activePanel = useAppSelector(selectActivePanel);
  const isUpdatingInstance = useAppSelector(selectIsUpdatingInstance);

  const openAddUsersModal = useCallback(() => {
    setIsAddUsersModalOpen(true);
  }, [setIsAddUsersModalOpen]);

  const closeAddUsersModal = useCallback(() => {
    setIsAddUsersModalOpen(false);
  }, [setIsAddUsersModalOpen]);

  const openManageUserModal = useCallback(() => {
    setIsManageUserModalOpen(true);
  }, [setIsManageUserModalOpen]);

  const closeManageUserModal = useCallback(() => {
    setIsManageUserModalOpen(false);
  }, [setIsManageUserModalOpen]);

  const restoreClassicIA = useCallback(async () => {
    setShowRestoreIA(false);

    // Switch to Classic IA.
    await dispatch(switchExperience({ new_ia: false, instanceId, navigate }));
  }, [dispatch, instanceId, setShowRestoreIA, navigate]);

  const user = useAppSelector(getUser, shallowEqual);
  const planName = useAppSelector(selectPlan);
  const planTitle = PLAN_TITLES[planName] || planName;
  const showUpgrade = !isCpd && PLAN_UPGRADE[planName];
  const readOnly = useAppSelector(isInstanceReadOnly);
  const trialData = useAppSelector(selectPlusTrialData, shallowEqual);
  const isAccessControlFeature = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.ACCESS_CONTROL)
  );
  const region = useAppSelector(selectInstanceRegion);

  let planDetail = null;
  let trialReminder = null;
  if (planName === PLAN_NAMES.PLUS_TRIAL) {
    const trialManager = new PlusTrialManager(instanceId, trialData);
    const daysLeft = trialManager.daysRemaining();
    if (daysLeft >= 0) {
      planDetail = intl.formatMessage(messages.daysLeft, { daysLeft });
      if (trialManager.reminderNeeded(daysLeft)) {
        trialReminder = {
          daysLeft,
          bumpThreshold: () => {
            trialManager.delayReminder(daysLeft);

            // After setting the delay reminder a few days hence and saving
            // the new value in local storage, force an update so that the
            // trial reminder pop-up is no longer shown.
            forceUpdate();
          }
        };
      }
    }
  }

  return {
    account,
    activePanel,
    closeAddUsersModal,
    closeManageUserModal,
    handleOpenWhatsNewPanel,
    instanceId,
    isAccessControlFeature,
    isAddUsersModalOpen,
    isManageUserModalOpen,
    isUpdatingInstance,
    onLogout: doLogout,
    openAddUsersModal,
    openManageUserModal,
    planDetail,
    planName,
    planTitle,
    readOnly,
    region,
    restoreClassicIA,
    setActivePanel,
    setShowAboutModal,
    setShowCreateAssistantModal,
    setShowInstancePicker,
    setShowPlanInfo,
    setShowRestoreIA,
    setShowWhatsNewPanel,
    showAboutModal,
    showCreateAssistantModal,
    showInstancePicker,
    showPlanInfo,
    showRestoreIA,
    showWhatsNewPanel,
    showUpgrade,
    trialData,
    trialReminder,
    user
  };
};
